/**
 * DateComponents 层 UI 专用工具（快捷选项配置等）。
 * 日期领域纯函数请从 `@dbl/core/date` 引用。
 */
import moment from 'moment/moment';
import { getLastDateRange } from '@/pages/datalab/utils/utils';

export const getDatalabShortcut = (startOfTime: any, latestDate: any) => {
  const { date, month, week } = latestDate || {};

  const latestDataObj = {
    name: 'Latest Data',
    nameCN: '最新数据',
  };
  const allOfTimeObj = {
    name: 'All of time',
    nameCN: '所有时间',
  };

  const latestDateData = {
    ...latestDataObj,
    type: 'lasteDateDate',
    value: [moment(date), moment(date)],
    timeType: 'date',
    onClick: () => [moment(date), moment(date)],
  };
  const latestWeekData = {
    ...latestDataObj,
    type: 'lasteDateWeek',
    timeType: 'week',
    value: [moment(week).weekday(0), moment(week).weekday(6)],
    onClick: () => [moment(week).weekday(0), moment(week).weekday(6)],
  };
  const latestMonthData = {
    ...latestDataObj,
    type: 'lasteDateMonth',
    timeType: 'month',
    value: [
      moment(month).subtract(0, 'month').startOf('month'),
      moment(month).subtract(0, 'month').endOf('month'),
    ],
    onClick: () => [
      moment(month).subtract(0, 'month').startOf('month'),
      moment(month).subtract(0, 'month').endOf('month'),
    ],
  };

  const allDateOfTime = {
    ...allOfTimeObj,
    type: 'allOfTimeDate',
    timeType: 'date',
    value: [moment(startOfTime), moment().subtract(1, 'day')],
    onClick: () => [moment(startOfTime), moment().subtract(1, 'day')],
  };

  const getHourValue = (lastCount: number | string, dateType: string) => {
    const value = getLastDateRange(lastCount, dateType);
    return value?.map(item => moment(item));
  };

  const hourArray = [
    {
      name: 'Last 1 hour',
      nameCN: '最近1小时',
      type: 'last1Hour',
      timeType: 'hour',
      value: getHourValue(1, 'hour'),
      onClick: () => getHourValue(1, 'hour'),
    },
    {
      name: 'Last 6 hours',
      nameCN: '最近6小时',
      type: 'last6Hours',
      timeType: 'hour',
      value: getHourValue(6, 'hour'),
      onClick: () => getHourValue(6, 'hour'),
    },
    {
      name: 'Last 12 hours',
      nameCN: '最近12小时',
      type: 'last12Hours',
      timeType: 'hour',
      value: getHourValue(12, 'hour'),
      onClick: () => getHourValue(12, 'hour'),
    },
    {
      name: 'Last 24 hours',
      nameCN: '最近24小时',
      type: 'last24Hours',
      timeType: 'hour',
      value: getHourValue(24, 'hour'),
      onClick: () => getHourValue(24, 'hour'),
    },
    {
      name: 'Last 48 hours',
      nameCN: '最近48小时',
      type: 'last48Hours',
      timeType: 'hour',
      value: getHourValue(48, 'hour'),
      onClick: () => getHourValue(48, 'hour'),
    },
    {
      name: 'Today',
      nameCN: '今天',
      type: 'hourToday',
      timeType: 'date',
      value: [moment().startOf('day'), getHourValue(1, 'hour')[1]],
      onClick: () => [moment().startOf('day'), getHourValue(1, 'hour')[1]],
    },
  ];

  let dateArray = [
    {
      name: 'Yesterday',
      nameCN: '昨天',
      type: 'yesterday',
      timeType: 'date',
      value: [moment().subtract(1, 'day'), moment().subtract(1, 'day')],
      onClick: () => [moment().subtract(1, 'day'), moment().subtract(1, 'day')],
    },
    {
      name: 'Today',
      nameCN: '今天',
      type: 'today',
      timeType: 'date',
      value: [moment().subtract(0, 'day'), moment().subtract(0, 'day')],
      onClick: () => [moment().subtract(0, 'day'), moment().subtract(0, 'day')],
    },
    {
      name: 'Last 7 days',
      nameCN: '最近7天',
      type: 'last7Days',
      timeType: 'date',
      value: [moment().subtract(7, 'day'), moment().subtract(1, 'day')],
      onClick: () => [moment().subtract(7, 'day'), moment().subtract(1, 'day')],
    },
    {
      name: 'Last 28 days',
      nameCN: '最近28天',
      type: 'last28Days',
      timeType: 'date',
      value: [moment().subtract(28, 'day'), moment().subtract(1, 'day')],
      onClick: () => [moment().subtract(28, 'day'), moment().subtract(1, 'day')],
    },
    {
      name: 'Last 30 days',
      nameCN: '最近30天',
      type: 'last30Days',
      timeType: 'date',
      value: [moment().subtract(30, 'day'), moment().subtract(1, 'day')],
      onClick: () => [moment().subtract(30, 'day'), moment().subtract(1, 'day')],
    },
    {
      name: 'Last 90 days',
      nameCN: '最近90天',
      type: 'last90Days',
      timeType: 'date',
      value: [moment().subtract(90, 'day'), moment().subtract(1, 'day')],
      onClick: () => [moment().subtract(90, 'day'), moment().subtract(1, 'day')],
    },
    {
      name: 'Last 180 days',
      nameCN: '最近180天',
      type: 'last180Days',
      timeType: 'date',
      value: [moment().subtract(180, 'day'), moment().subtract(1, 'day')],
      onClick: () => [moment().subtract(180, 'day'), moment().subtract(1, 'day')],
    },
    {
      name: 'Last 365 days',
      nameCN: '最近365天',
      type: 'last365Days',
      timeType: 'date',
      value: [moment().subtract(365, 'day'), moment().subtract(1, 'day')],
      onClick: () => [moment().subtract(365, 'day'), moment().subtract(1, 'day')],
    },
    {
      name: 'Month to date',
      nameCN: '本月',
      type: 'monthToDate',
      timeType: 'date',
      value: [moment().startOf('month'), moment().subtract(1, 'day')],
      onClick: () => [moment().startOf('month'), moment().subtract(1, 'day')],
    },
    {
      name: 'Year to date',
      nameCN: '今年',
      type: 'yearToDate',
      timeType: 'date',
      value: [moment().startOf('year'), moment().subtract(1, 'day')],
      onClick: () => [moment().startOf('year'), moment().subtract(1, 'day')],
    },
  ];

  let weekArray = [
    {
      name: 'Current week',
      nameCN: '本周',
      type: 'currentWeek',
      timeType: 'week',
      value: [moment().weekday(-7 * 0), moment().weekday(0)],
      onClick: () => [moment().weekday(-7 * 0), moment().weekday(0)],
    },
    {
      name: 'Last 1 week',
      nameCN: '最近1周',
      type: 'last1Weeks',
      timeType: 'week',
      value: [moment().weekday(-7 * 1), moment().weekday(-1)],
      onClick: () => [moment().weekday(-7 * 1), moment().weekday(-1)],
    },
    {
      name: 'Last 4 weeks',
      nameCN: '最近4周',
      type: 'last4Weeks',
      timeType: 'week',
      value: [moment().weekday(-7 * 4), moment().weekday(-1)],
      onClick: () => [moment().weekday(-7 * 4), moment().weekday(-1)],
    },
    {
      name: 'Last 12 weeks',
      nameCN: '最近12周',
      type: 'last12Weeks',
      timeType: 'week',
      value: [moment().weekday(-7 * 12), moment().weekday(-1)],
      onClick: () => [moment().weekday(-7 * 12), moment().weekday(-1)],
    },
    {
      name: 'Last 24 weeks',
      nameCN: '最近24周',
      type: 'last24Weeks',
      timeType: 'week',
      value: [moment().weekday(-7 * 24), moment().weekday(-1)],
      onClick: () => [moment().weekday(-7 * 24), moment().weekday(-1)],
    },
    {
      name: 'Year to date',
      nameCN: '今年',
      type: 'yearToWeek',
      timeType: 'week',
      value: [moment().startOf('year').startOf('week'), moment().weekday(6)],
      onClick: () => [moment().startOf('year').startOf('week'), moment().weekday(6)],
    },
  ];

  let monthArray = [
    {
      name: 'Current month',
      nameCN: '本月',
      type: 'currentMonth',
      timeType: 'month',
      value: [
        moment().subtract(0, 'month').startOf('month'),
        moment().subtract(0, 'month').endOf('month'),
      ],
      onClick: () => [
        moment().subtract(0, 'month').startOf('month'),
        moment().subtract(0, 'month').endOf('month'),
      ],
    },
    {
      name: 'Last 1 month',
      nameCN: '最近1个月',
      type: 'last1Months',
      timeType: 'month',
      value: [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
      onClick: () => [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
    },
    {
      name: 'Last 3 months',
      nameCN: '最近3个月',
      type: 'last3Months',
      timeType: 'month',
      value: [
        moment().subtract(3, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
      onClick: () => [
        moment().subtract(3, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
    },
    {
      name: 'Last 6 months',
      nameCN: '最近6个月',
      type: 'last6Months',
      timeType: 'month',
      value: [
        moment().subtract(6, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
      onClick: () => [
        moment().subtract(6, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
    },
    {
      name: 'Last 12 months',
      nameCN: '最近12个月',
      type: 'last12Months',
      timeType: 'month',
      value: [
        moment().subtract(12, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
      onClick: () => [
        moment().subtract(12, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
    },
    {
      name: 'Last 24 months',
      nameCN: '最近24个月',
      type: 'last24Months',
      timeType: 'month',
      value: [
        moment().subtract(24, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
      onClick: () => [
        moment().subtract(24, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
    },
    {
      name: 'Year to date',
      nameCN: '今年',
      type: 'yearToMonth',
      timeType: 'month',
      value: [moment().startOf('year'), moment().endOf('month')],
      onClick: () => [moment().startOf('year'), moment().endOf('month')],
    },
  ];

  if (JSON.stringify(latestDate) !== '{}') {
    dateArray = [latestDateData, ...dateArray];
    weekArray = [latestWeekData, ...weekArray];
    monthArray = [latestMonthData, ...monthArray];
  }
  if (startOfTime !== '') {
    dateArray = [...dateArray, allDateOfTime];
    weekArray = [...weekArray];
    monthArray = [...monthArray];
  }

  return {
    hour: hourArray,
    day: dateArray,
    date: dateArray,
    week: weekArray,
    month: monthArray,
  };
};
